import React from "react";
import { Box, Button, Stack, useTheme, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  searchBar: {
    background: "rgba(72, 72, 72, 0.50)",
    backdropFilter: "blur(2px)",
    WebkitBackdropFilter: "blur(2px)",
    padding: "8px 20px",
    borderRadius: "16px",
    border: "2px solid transparent",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: "72px",
    transition: "border-color 0.2s",
    "&:hover": {
      borderRadius: "16px",
      border: "2px solid #007CBF",
    },
    "&:focus-within": {
      borderRadius: "16px",
      border: "2px solid #007CBF",
    },
    [theme.breakpoints.down("md")]: {
      padding: "13px 16px",
      height: "56px",
      borderRadius: "8px",
      "&:hover": {
        borderRadius: "8px",
        border: "2px solid #007CBF",
      },
      "&:focus-within": {
        borderRadius: "8px",
        border: "2px solid #007CBF",
      },
    },
  },
  searchBarLight: {
    borderRadius: "16px",
    border: "2px solid #D0D0D0",
    background: "#FFF",
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
    "&:hover": {
      borderRadius: "16px",
      border: "2px solid #007CBF",
    },
    "&:focus-within": {
      borderRadius: "16px",
      border: "2px solid #007CBF",
    },
    [theme.breakpoints.down("md")]: {
      borderRadius: "16px",
      "&:hover": {
        borderRadius: "16px",
        border: "2px solid #007CBF",
      },
      "&:focus-within": {
        borderRadius: "16px",
        border: "2px solid #007CBF",
      },
    },
  },
  searchInput: {
    flexGrow: 1,
    minWidth: "0px",
    padding: "0px",
    border: "0px",
    background: "transparent",
    backgroundColor: "transparent",
    color: "#D0D0D0",
    fontFamily: "proxima-soft, sans-serif",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "34px",
    "&::placeholder": {
      color: "#D0D0D0",
      fontFamily: "proxima-soft, sans-serif",
      fontSize: "18px",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "34px",
      opacity: 1,
    },
    "&:focus::placeholder": {
      opacity: 0,
    },
    "&:focus": {
      outline: "none",
    },
    "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 1000px transparent inset",
      WebkitTextFillColor: "#D0D0D0",
      caretColor: "#D0D0D0",
      transition: "background-color 5000s ease-in-out 0s",
    },
  },
  searchInputLight: {
    color: "#191919",
    "&::placeholder": {
      color: "#747474",
    },
    "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus": {
      WebkitTextFillColor: "#191919",
      caretColor: "#191919",
    },
  },
  buttonGroup: {
    flexShrink: 0,
    marginLeft: "auto",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
  },
  clearButton: {
    height: "56px",
    minHeight: "56px",
    padding: "0 16px",
    textTransform: "none",
    color: "#D0D0D0",
    backgroundColor: "transparent",
    boxShadow: "none",
    fontFamily: "proxima-soft, sans-serif",
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: "20px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      boxShadow: "none",
    },
    "&.MuiButton-root": {
      minWidth: 0,
    },
    [theme.breakpoints.down("md")]: {
      height: "40px",
      minHeight: "40px",
      fontSize: "16px",
      padding: "0 12px",
    },
  },
  clearButtonLight: {
    color: "#484848",
    "&:hover": {
      backgroundColor: "rgba(25, 25, 25, 0.04)",
    },
  },
  buttons: {
    height: "56px",
    minHeight: "56px",
    maxHeight: "56px",
    boxSizing: "border-box",
    marginLeft: "16px",
    "&.MuiButton-root": {
      height: "56px",
      minHeight: "56px",
      maxHeight: "56px",
    },
    "&.MuiButton-sizeLarge": {
      height: "56px",
      minHeight: "56px",
      maxHeight: "56px",
      padding: "0 22px",
    },
    [theme.breakpoints.down("md")]: {
      height: "40px",
      borderRadius: "8px",
      fontSize: "16px",
      lineHeight: "26px",
      marginLeft: "8px",
      padding: "7px 16px",
    },
  },
}));

type FAQSearchVariant = "landing" | "light";

interface FAQSearchProps {
  inputText: string;
  onInputChange: (newText: string) => void;
  onSearch: (e: any) => void;
  onClear: () => void;
  variant?: FAQSearchVariant;
}

export const FAQSearch = ({
  inputText,
  onInputChange,
  onSearch,
  onClear,
  variant = "landing",
}: FAQSearchProps) => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLight = variant === "light";
  const showClear = inputText.length > 0;

  const searchButton = (
    <Button variant="contained" className={classes.buttons} onClick={onSearch}>
      Search<i className="fa-solid fa-search" style={{ marginLeft: "8px" }}></i>
    </Button>
  );

  const clearButton = showClear ? (
    <Button
      variant="text"
      className={`${classes.clearButton} ${isLight ? classes.clearButtonLight : ""}`}
      onClick={onClear}
      aria-label="Clear search">
      Clear
    </Button>
  ) : null;

  return (
    <>
      <Box className={`${classes.searchBar} ${isLight ? classes.searchBarLight : ""}`}>
        <input
          value={inputText}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onSearch(e);
            }
          }}
          className={`${classes.searchInput} ${isLight ? classes.searchInputLight : ""}`}
          placeholder="Search FAQs"
        />
        {!isMobile && (
          <Box className={classes.buttonGroup}>
            {searchButton}
            {clearButton}
          </Box>
        )}
      </Box>
      {isMobile && (
        <Stack direction="row" sx={{ marginTop: "8px" }}>
          <Box className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="primary"
              className={classes.buttons}
              onClick={onSearch}>
              Search<i className="fa-solid fa-search" style={{ marginLeft: "8px" }}></i>
            </Button>
            {clearButton}
          </Box>
        </Stack>
      )}
    </>
  );
};
